import { Dayjs } from 'dayjs'
import { Controller, useFormContext } from 'react-hook-form'

import { FatigueModel, FatigueModelChangeStateEnum } from '@/entities/schedules/models/FatigueModel'
import { BreakConfigurationField } from '@/shared/components/BreakConfigurationField/BreakConfigurationField'
import { SolverIcon } from '@/shared/components/Icon/SolverIcon'
import { TimePicker } from '@/shared/components/TimePicker/TimePicker'
import { timeHhMmSsToDayjs, timeHhMmToMins } from '@/shared/lib/timeHelper'
import { pxToRem } from '@/shared/styles/themes'
import { Box, Checkbox, FormControlLabel, Grid, InputLabel, Radio, RadioGroup, Typography } from '@mui/material'

import { BulkEditCheckbox } from '../BulkEditCheckbox'

type FatigueManagementTabProps = {
  isReadOnly: boolean
  isBulkEdit: boolean
  standardFatigueSettings: {
    breakDuration: Dayjs | null
    fatigueModels: FatigueModel[]
  }
}

export const FatigueManagementTab = ({
  isReadOnly,
  isBulkEdit,
  standardFatigueSettings,
}: FatigueManagementTabProps) => {
  const { getValues, control, setValue, watch } = useFormContext()

  const watchFatigueModelType = watch('fatigueModelType')

  // Handle fatigue model type changes
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFatigueModelChange = (value: number, onChange: (...event: any[]) => void) => {
    onChange(value)

    if (value === 0) {
      setValue('breakDuration', timeHhMmSsToDayjs('00:00'))

      const emptyFatigueModels = (getValues('fatigueModels') as FatigueModel[]).map(model => ({
        ...model,
        state: FatigueModelChangeStateEnum.Deleted,
      }))

      setValue('fatigueModels', emptyFatigueModels)
    }
    else if (value === 1) {
      setValue('breakDuration', standardFatigueSettings.breakDuration)

      // Remove existing fatigue models
      let newFatigueModels = (getValues('fatigueModels') as FatigueModel[]).map(model => ({
        ...model,
        state: FatigueModelChangeStateEnum.Deleted,
      }))

      // Add standard fatigue models to newFatigueModels
      newFatigueModels = newFatigueModels.concat(
        standardFatigueSettings.fatigueModels.map(model => ({
          ...model,
          state: FatigueModelChangeStateEnum.Added,
        })),
      )

      setValue('fatigueModels', newFatigueModels)
    }
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="body1"
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 500,
            mt: 1,
            mb: 1,
          }}
        >
          Fatigue Management
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name="fatigueModelType"
          control={control}
          render={({ field }) => (
            <Box>
              <InputLabel sx={{ mb: 2 }}>Policy</InputLabel>
              <RadioGroup
                {...field}
                value={field.value}
                onChange={(e) => handleFatigueModelChange(Number(e.target.value), field.onChange)}
              >
                <FormControlLabel
                  value={0}
                  control={<Radio disabled={isReadOnly} />}
                  label="No Breaks"
                />
                <FormControlLabel
                  value={1}
                  control={<Radio disabled={isReadOnly} />}
                  label="Standard Breaks"
                />
                <FormControlLabel
                  value={2}
                  control={<Radio disabled={isReadOnly} />}
                  label="Custom Breaks"
                />
              </RadioGroup>
              {isBulkEdit && <BulkEditCheckbox name="fatigueModelType" />}
            </Box>
          )}
        />
      </Grid>

      {/* Allow Breaks During Transit */}
      {watchFatigueModelType !== 0 && (
        <Grid item xs={12}>
          <Controller
            name="allowBreaksDuringTransit"
            control={control}
            render={({ field }) => (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      disabled={isReadOnly}
                    />
                  }
                  label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SolverIcon />
                    Allow Breaks during Transit?
                  </Box>}
                />
                {isBulkEdit && <BulkEditCheckbox name="allowBreaksDuringTransit" />}
              </Box>
            )}
          />
        </Grid>
      )}

      {/* Break Duration */}
      <Grid item xs={12}>
        <Controller
          name="breakDuration"
          control={control}
          rules={{
            required: 'Break duration is required',
            validate: (value) => {
              if (watchFatigueModelType === 2 && value?.hour() === 0 && value?.minute() === 0) {
                return 'Break duration cannot be 00:00'
              }
            },
          }}
          render={({ field, fieldState }) => (
            <Box>
              <TimePicker
                {...field}
                label="Break Duration"
                disabled={isReadOnly || watchFatigueModelType === 1}
                format="HH:mm"
                views={['hours', 'minutes']}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!fieldState.error,
                    helperText: fieldState.error?.message,
                  },
                }}
              />
              {isBulkEdit && <BulkEditCheckbox name="breakDuration" />}
            </Box>
          )}
        />
      </Grid>

      {/* Break Configurations */}
      {watchFatigueModelType !== 0 && (
        <Grid item xs={12}>
          <Controller
            name="fatigueModels"
            control={control}
            rules={{
              validate: (value: FatigueModel[]) => {
                if (!value?.length) {
                  return 'At least one break configuration is required'
                }

                for (let i = 0; i < value.length; i++) {
                  if (value[i].numberOfBreaks <= 0 && value[i].state !== FatigueModelChangeStateEnum.Deleted) {
                    return '\'Number of Breaks\' must be greater than 0'
                  }

                  if (value[i].timeInterval === '00:00') {
                    return '\'Time Period\' must be greater than 00:00'
                  }
                }

                const validRows = value.filter(item => item.state !== FatigueModelChangeStateEnum.Deleted)

                for (let i = 1; i < validRows.length; i++) {
                  if (validRows[i].numberOfBreaks <= validRows[i - 1].numberOfBreaks) {
                    return '\'Number of Breaks\' should increase with each row'
                  }

                  // Check Time Period should increase with each row
                  const prevTime = timeHhMmToMins(validRows[i - 1].timeInterval)
                  const currTime = timeHhMmToMins(validRows[i].timeInterval)

                  if (currTime && prevTime && currTime <= prevTime) {
                    return '\'Time Period\' should increase with each row'
                  }
                }

                return true
              },
            }}
            render={({ field, fieldState }) => {
              return (
                <Box>
                  <Typography sx={{ mb: 2 }}>Break Configurations</Typography>
                  <BreakConfigurationField
                    {...field}
                    disabled={isReadOnly}
                    error={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                    isCustomBreaks={watchFatigueModelType === 2}
                  />
                  {isBulkEdit && <BulkEditCheckbox name="fatigueModels" />}
                </Box>
              )
            }}
          />
        </Grid>
      )}
    </Grid>
  )
}